import { useTranslation } from 'react-i18next';
import { ActionIcon, Alert, Box, Button, Group, ScrollArea, Skeleton, Stack, Text, Title, Tooltip } from '@mantine/core';
import { IconBellOff, IconVolume, IconVolumeOff } from '@tabler/icons-react';
import type { UseQueryResult } from '@tanstack/react-query';
import EmptyState from '@/components/common/EmptyState';
import { useReadAllNotifications, useUnreadCount } from '@/hooks/useNotifications';
import { setSoundEnabled, useSoundEnabled } from '@/lib/soundPreference';
import type { NotificationItem as Item } from '@/types/notifications';
import NotificationItem from './NotificationItem';
import touchTarget from '@/components/common/touchTarget.module.css';

interface NotificationPanelProps {
  readonly query: UseQueryResult<Item[]>;
  readonly onClose: () => void;
}

// Содержимое выпадающей панели колокольчика: шапка с «Прочитать все» и звуком + состояние данных
// (загрузка/ошибка/пусто/список). Высота ограничена, длинный список скроллится.
export default function NotificationPanel({ query, onClose }: NotificationPanelProps) {
  const { t } = useTranslation();
  const readAll = useReadAllNotifications();
  const items = query.data ?? [];
  const unread = useUnreadCount(items);
  const soundEnabled = useSoundEnabled();
  const soundLabel = soundEnabled ? t('app.notifications.soundMute') : t('app.notifications.soundUnmute');

  return (
    <Box>
      <Group justify="space-between" px="md" py="sm" wrap="nowrap">
        <Title order={5}>{t('app.notifications.title')}</Title>
        <Group gap={4} wrap="nowrap">
          <Button
            variant="subtle"
            size="compact-sm"
            disabled={unread === 0 || readAll.isPending}
            onClick={() => readAll.mutate()}
          >
            {t('app.notifications.markAllRead')}
          </Button>
          <Tooltip label={soundLabel} withinPortal={false}>
            <ActionIcon
              variant="subtle"
              color="gray"
              className={touchTarget.target}
              onClick={() => setSoundEnabled(!soundEnabled)}
              aria-label={soundLabel}
            >
              {soundEnabled ? <IconVolume size={18} /> : <IconVolumeOff size={18} />}
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {query.isLoading ? (
        <Stack gap="sm" px="md" pb="md">
          <Skeleton h={48} />
          <Skeleton h={48} />
          <Skeleton h={48} />
        </Stack>
      ) : query.isError ? (
        <Box px="md" pb="md">
          <Alert color="red" variant="light">
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm">{t('app.notifications.loadError')}</Text>
              <Button variant="subtle" size="compact-sm" onClick={() => query.refetch()}>
                {t('common.retry')}
              </Button>
            </Group>
          </Alert>
        </Box>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<IconBellOff size={30} />}
          title={t('app.notifications.empty')}
          description={t('app.notifications.emptyHint')}
          decorated={false}
        />
      ) : (
        <ScrollArea.Autosize mah="min(70vh, 460px)" type="auto">
          <Stack gap={0}>
            {items.map((item) => (
              <NotificationItem key={item.id} item={item} onClose={onClose} />
            ))}
          </Stack>
        </ScrollArea.Autosize>
      )}
    </Box>
  );
}
